import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { ALL_STATUSES } from "../lib/api";
import { ArrowLeft, Users, Building2, Download, Mail, ChevronsUpDown, X, Lock } from "lucide-react";

export default function Pipeline() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const isEnterprise = isOwner || user?.subscription_tier === "enterprise";
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [jobId, setJobId] = useState("");
  const [companyId, setCompanyId] = useState(() => (user && user.role !== "owner" ? (user.company_id || "") : ""));
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(null);  // null | "status" | "email"
  const [bulkForm, setBulkForm] = useState({ status: "Reviewed", note: "", subject: "", body_html: "", send_emails: true });
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  // Custom Pipeline and Single Email States
  const [pipelineStages, setPipelineStages] = useState(ALL_STATUSES);
  const [singleEmailApp, setSingleEmailApp] = useState(null);
  const [singleEmailForm, setSingleEmailForm] = useState({ subject: "", body_html: "" });
  const [singleEmailSubmitting, setSingleEmailSubmitting] = useState(false);

  const loadStages = async () => {
    const compId = user?.company_id || companyId;
    if (compId) {
      try {
        const { data } = await api.get(`/companies/${compId}/pipeline-stages`);
        setPipelineStages(data.stages || ALL_STATUSES);
      } catch {
        setPipelineStages(ALL_STATUSES);
      }
    } else {
      setPipelineStages(ALL_STATUSES);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [a, j, c] = await Promise.all([
        api.get("/applications"),
        api.get("/jobs?mine=true").catch(() => ({ data: [] })),
        api.get("/companies").catch(() => ({ data: [] })),
      ]);
      setApps(a.data || []);
      setJobs(j.data || []);
      setCompanies(c.data || []);
      await loadStages();
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [user?.user_id]);

  const filtered = useMemo(() => {
    let list = apps;
    if (companyId) list = list.filter((x) => x.company_id === companyId);
    if (jobId) list = list.filter((x) => x.job_id === jobId);
    return list;
  }, [apps, jobId, companyId]);

  const byStatus = useMemo(() => {
    const m = {};
    pipelineStages.forEach((s) => { m[s] = []; });
    filtered.forEach((a) => {
      const st = pipelineStages.includes(a.status) ? a.status : pipelineStages[0];
      if (st) (m[st] = m[st] || []).push(a);
    });
    return m;
  }, [filtered, pipelineStages]);

  const runSingleEmail = async (e) => {
    e?.preventDefault?.();
    setSingleEmailSubmitting(true);
    try {
      await api.post("/applications/bulk/email", {
        application_ids: [singleEmailApp.application_id],
        subject: singleEmailForm.subject,
        body_html: singleEmailForm.body_html,
      });
      alert("Email successfully sent to candidate!");
      setSingleEmailApp(null);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setSingleEmailSubmitting(false); }
  };

  const handleSelectClippedEmail = (templateKey) => {
    if (!templateKey) return;
    const name = singleEmailApp?.candidate_name || "Applicant";
    const jobTitle = singleEmailApp?.job_title || "the role";
    
    const templates = {
      interview: {
        subject: `Interview Invitation: ${jobTitle}`,
        body_html: `<p>Hi ${name},</p>\n<p>We have reviewed your application for <strong>${jobTitle}</strong> and would love to invite you to an interview.</p>\n<p>Please let us know your availability over the coming week.</p>\n<p>Best regards,<br/>The Hiring Team</p>`
      },
      under_review: {
        subject: `Application Update: ${jobTitle}`,
        body_html: `<p>Hi ${name},</p>\n<p>Thank you for applying for <strong>${jobTitle}</strong>. We wanted to let you know that your application is currently under review by our hiring team.</p>\n<p>We will be in touch with further updates soon.</p>\n<p>Best regards,<br/>The Hiring Team</p>`
      },
      rejection: {
        subject: `Update regarding your application for ${jobTitle}`,
        body_html: `<p>Hi ${name},</p>\n<p>Thank you for your interest in the <strong>${jobTitle}</strong> position at our company.</p>\n<p>After careful consideration, we regret to inform you that we will not be moving forward with your application at this time.</p>\n<p>We wish you the very best in your job search.</p>\n<p>Best regards,<br/>The Hiring Team</p>`
      },
      offer: {
        subject: `Offer of Employment: ${jobTitle}`,
        body_html: `<p>Hi ${name},</p>\n<p>We are absolutely thrilled to offer you the position of <strong>${jobTitle}</strong>!</p>\n<p>Our team was incredibly impressed by your background, skills, and experience.</p>\n<p>Please find attached your offer details. We look forward to welcoming you to our team!</p>\n<p>Best regards,<br/>The Hiring Team</p>`
      }
    };
    
    const tpl = templates[templateKey];
    if (tpl) {
      setSingleEmailForm({ subject: tpl.subject, body_html: tpl.body_html });
    }
  };

  const move = async (appId, status) => {
    try {
      await api.put(`/applications/${appId}/status`, { status });
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const removeApp = async (appId) => {
    if (!window.confirm("Remove this applicant?")) return;
    try {
      await api.delete(`/applications/${appId}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const toggleSelect = (appId) => {
    const next = new Set(selected);
    if (next.has(appId)) next.delete(appId); else next.add(appId);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(filtered.map((a) => a.application_id)));
  const clearSelection = () => setSelected(new Set());

  const requireEnterpriseOr = (action) => {
    if (!isEnterprise) { setPaywallOpen(true); return; }
    action();
  };

  const runBulkStatus = async (e) => {
    e?.preventDefault?.();
    setBulkSubmitting(true);
    try {
      const { data } = await api.post("/applications/bulk/status", {
        application_ids: Array.from(selected),
        status: bulkForm.status,
        note: bulkForm.note,
        send_emails: bulkForm.send_emails,
      });
      alert(`Updated ${data.updated} · Skipped ${data.skipped} · Emails queued ${data.emails_queued}`);
      setBulkMode(null); clearSelection(); load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBulkSubmitting(false); }
  };

  const runBulkEmail = async (e) => {
    e?.preventDefault?.();
    setBulkSubmitting(true);
    try {
      const { data } = await api.post("/applications/bulk/email", {
        application_ids: Array.from(selected),
        subject: bulkForm.subject,
        body_html: bulkForm.body_html,
      });
      alert(`Sent ${data.sent} · Skipped ${data.skipped}`);
      setBulkMode(null); clearSelection();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBulkSubmitting(false); }
  };

  const runBulkExport = async () => {
    setBulkSubmitting(true);
    try {
      const res = await api.post("/applications/bulk/export",
        { application_ids: Array.from(selected) },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `applications_export_${Date.now()}.csv`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      clearSelection();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setBulkSubmitting(false); }
  };

  const currentCompany = companies.find((c) => c.company_id === companyId);

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
      <Link to="/recruiter" className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[var(--brand-primary)]">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <div className="mt-4 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><Users className="w-3 h-3" /> Pipeline {currentCompany && <>· <span className="text-[var(--brand-primary)]">{currentCompany.name}</span></>}</div>
          <h1 className="font-display font-black text-5xl mt-3 tracking-tight">9-stage pipeline.</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">{isOwner ? "Pick a company to scope the pipeline; default shows all." : "Applicants for your company, grouped by stage."}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          {isOwner && (
            <div>
              <div className="label-eyebrow flex items-center gap-1.5 mb-1"><Building2 className="w-3 h-3" /> Company</div>
              <select
                data-testid="pipeline-company-filter"
                value={companyId}
                onChange={(e) => { setCompanyId(e.target.value); setJobId(""); }}
                className="border border-[#0F0F0F] px-3 py-2 bg-transparent text-sm min-w-[220px]"
              >
                <option value="">All companies</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <div className="label-eyebrow mb-1">Role</div>
            <select
              data-testid="pipeline-job-filter"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              className="border border-[#0F0F0F] px-3 py-2 bg-transparent text-sm min-w-[220px]"
            >
              <option value="">{`All roles (${filtered.length})`}</option>
              {jobs.filter((j) => !companyId || j.company_id === companyId).map((j) => (
                <option key={j.job_id} value={j.job_id}>{`${j.title} — ${j.company}`}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-[#5C5C5C]">Loading…</div>
      ) : (
        <div className="mt-8 overflow-x-auto">
          <div className="grid grid-flow-col auto-cols-[280px] gap-3 pb-4">
            {pipelineStages.map((s) => (
              <div key={s} data-testid={`pipeline-col-${s.replace(/\s+/g, "-")}`} className="bg-[#F3F2EB] border border-[#E2E0D8] flex flex-col min-h-[60vh]">
                <div className="px-3 py-2 border-b border-[#E2E0D8] flex items-center justify-between bg-[#FDFCF8]">
                  <StatusBadge status={s} />
                  <span className="text-xs text-[#5C5C5C]">{byStatus[s].length}</span>
                </div>
                <div className="p-2 space-y-2 flex-1 overflow-y-auto max-h-[70vh]">
                  {byStatus[s].length === 0 && (
                    <div className="text-xs text-[#5C5C5C] italic text-center py-6">empty</div>
                  )}
                  {byStatus[s].map((a) => {
                    const isSel = selected.has(a.application_id);
                    return (
                    <div key={a.application_id} data-testid={`card-${a.application_id}`} className={`bg-[#FDFCF8] border ${isSel ? "border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary)]/30" : "border-[#E2E0D8]"} p-3 group`}>
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleSelect(a.application_id)}
                          data-testid={`select-${a.application_id}`}
                          className="mt-1 accent-[var(--brand-primary)]"
                          title="Select for bulk actions"
                        />
                        <Link to={`/recruiter/applications/${a.application_id}`} className="block flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <div className="font-semibold text-sm truncate">{a.candidate_name || a.candidate_email}</div>
                            {a._anonymous && <span data-testid={`anon-badge-${a.application_id}`} title="Anonymous review enabled — identity hidden until Interview" className="text-[9px] uppercase tracking-widest font-semibold bg-[#0F0F0F] text-[#FDFCF8] px-1 py-0.5">anon</span>}
                          </div>
                          <div className="text-xs text-[#5C5C5C] truncate">{a.job_title}</div>
                          <div className="text-[10px] text-[#5C5C5C] mt-1 uppercase tracking-widest">{new Date(a.created_at).toLocaleDateString()}</div>
                        </Link>
                      </div>
                      <div className="flex gap-1 mt-2">
                        <select
                          value={a.status}
                          onChange={(e) => move(a.application_id, e.target.value)}
                          data-testid={`status-select-${a.application_id}`}
                          className="flex-1 text-xs border border-[#E2E0D8] px-1.5 py-1 bg-transparent opacity-70 group-hover:opacity-100 transition"
                        >
                          {pipelineStages.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        <button
                          onClick={() => {
                            setSingleEmailApp(a);
                            setSingleEmailForm({ subject: `Regarding your application for ${a.job_title}`, body_html: `<p>Hi ${a.candidate_name || "there"},</p>` });
                          }}
                          data-testid={`email-applicant-${a.application_id}`}
                          title="Email candidate"
                          className="text-xs px-1.5 py-1 border border-[#E2E0D8] opacity-50 hover:opacity-100 hover:border-blue-600 hover:text-blue-700 bg-white transition"
                        >
                          ✉️
                        </button>
                        <button onClick={() => removeApp(a.application_id)} data-testid={`remove-${a.application_id}`} title="Remove applicant" className="text-xs px-1.5 py-1 border border-[#E2E0D8] opacity-50 hover:opacity-100 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition">✕</button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bulk actions sticky toolbar */}
      {selected.size > 0 && (
        <div data-testid="bulk-toolbar" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#0F0F0F] text-[#FDFCF8] border border-[#0F0F0F] shadow-2xl px-5 py-3 flex items-center gap-3 flex-wrap">
          <div className="font-display font-bold text-sm" data-testid="bulk-count">{selected.size} selected</div>
          <button onClick={selectAll} className="text-xs underline opacity-80 hover:opacity-100">Select all visible</button>
          <button onClick={clearSelection} className="text-xs underline opacity-80 hover:opacity-100" data-testid="bulk-clear">Clear</button>
          <span className="w-px h-5 bg-[#FDFCF8]/30" />
          <button
            data-testid="bulk-status-btn"
            onClick={() => requireEnterpriseOr(() => setBulkMode("status"))}
            className="inline-flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-widest hover:text-[var(--brand-primary)]"
          >
            <ChevronsUpDown className="w-4 h-4" /> Change status
            {!isEnterprise && <Lock className="w-3 h-3" />}
          </button>
          <button
            data-testid="bulk-email-btn"
            onClick={() => requireEnterpriseOr(() => setBulkMode("email"))}
            className="inline-flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-widest hover:text-[var(--brand-primary)]"
          >
            <Mail className="w-4 h-4" /> Email selected
            {!isEnterprise && <Lock className="w-3 h-3" />}
          </button>
          <button
            data-testid="bulk-export-btn"
            disabled={bulkSubmitting}
            onClick={() => requireEnterpriseOr(runBulkExport)}
            className="inline-flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-widest hover:text-[var(--brand-primary)] disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Export CSV
            {!isEnterprise && <Lock className="w-3 h-3" />}
          </button>
        </div>
      )}

      {/* Bulk status modal */}
      {bulkMode === "status" && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center px-4" data-testid="bulk-status-modal">
          <form onSubmit={runBulkStatus} className="bg-[#FDFCF8] border border-[#0F0F0F] max-w-md w-full p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Bulk status — {selected.size} applicant{selected.size === 1 ? "" : "s"}</div>
              <button type="button" onClick={() => setBulkMode(null)}><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">New status</label>
              <select data-testid="bulk-status-value" value={bulkForm.status} onChange={(e) => setBulkForm({ ...bulkForm, status: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2">
                {ALL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Note (optional, audit log only)</label>
              <input data-testid="bulk-status-note" value={bulkForm.note} onChange={(e) => setBulkForm({ ...bulkForm, note: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input data-testid="bulk-status-send-emails" type="checkbox" checked={bulkForm.send_emails} onChange={(e) => setBulkForm({ ...bulkForm, send_emails: e.target.checked })} />
              Trigger custom template emails (if configured)
            </label>
            <button type="submit" disabled={bulkSubmitting} data-testid="bulk-status-submit" className="btn-terra disabled:opacity-40 w-full">
              {bulkSubmitting ? "Applying…" : "Apply to all selected"}
            </button>
          </form>
        </div>
      )}

      {/* Bulk email modal */}
      {bulkMode === "email" && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center px-4" data-testid="bulk-email-modal">
          <form onSubmit={runBulkEmail} className="bg-[#FDFCF8] border border-[#0F0F0F] max-w-2xl w-full p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Bulk email — {selected.size} applicant{selected.size === 1 ? "" : "s"}</div>
              <button type="button" onClick={() => setBulkMode(null)}><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Subject</label>
              <input data-testid="bulk-email-subject" required value={bulkForm.subject} onChange={(e) => setBulkForm({ ...bulkForm, subject: e.target.value })} placeholder="An update on your application for {{job_title}}" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Message (HTML allowed)</label>
              <textarea data-testid="bulk-email-body" required rows={8} value={bulkForm.body_html} onChange={(e) => setBulkForm({ ...bulkForm, body_html: e.target.value })} placeholder="<p>Hi {{candidate_name}},</p><p>Thanks for applying to {{company_name}}.</p>" className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 font-mono text-xs" />
            </div>
            <div className="text-xs text-[#5C5C5C]">
              Variables: <code className="px-1 bg-[#E2E0D8]">{`{{candidate_name}}`}</code> <code className="px-1 bg-[#E2E0D8]">{`{{job_title}}`}</code> <code className="px-1 bg-[#E2E0D8]">{`{{company_name}}`}</code> <code className="px-1 bg-[#E2E0D8]">{`{{status}}`}</code>
            </div>
            <button type="submit" disabled={bulkSubmitting} data-testid="bulk-email-submit" className="btn-terra disabled:opacity-40 w-full">
              {bulkSubmitting ? "Sending…" : `Send to ${selected.size}`}
            </button>
          </form>
        </div>
      )}

      {/* Single Email Modal */}
      {singleEmailApp && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center px-4" data-testid="single-email-modal">
          <form onSubmit={runSingleEmail} className="bg-[#FDFCF8] border border-[#0F0F0F] max-w-2xl w-full p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow flex items-center gap-1.5 text-[var(--brand-primary)]">
                Email candidate — {singleEmailApp.candidate_name || singleEmailApp.candidate_email}
              </div>
              <button type="button" onClick={() => setSingleEmailApp(null)}><X className="w-4 h-4" /></button>
            </div>
            
            <div>
              <label className="label-eyebrow block mb-1.5">Choose Pre-Made Template (Clipped)</label>
              <select
                onChange={(e) => handleSelectClippedEmail(e.target.value)}
                data-testid="clipped-email-select"
                className="w-full border border-[#0F0F0F] bg-white px-3 py-2 text-sm text-[#0F0F0F]"
              >
                <option value="">Custom message (Write your own)...</option>
                <option value="interview">Interview Invitation</option>
                <option value="under_review">Application Under Review</option>
                <option value="rejection">Rejection Letter</option>
                <option value="offer">Offer Letter</option>
              </select>
            </div>

            <div>
              <label className="label-eyebrow block mb-1.5">Subject *</label>
              <input data-testid="single-email-subject" required value={singleEmailForm.subject} onChange={(e) => setSingleEmailForm({ ...singleEmailForm, subject: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 text-[#0F0F0F]" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Message (HTML allowed) *</label>
              <textarea data-testid="single-email-body" required rows={8} value={singleEmailForm.body_html} onChange={(e) => setSingleEmailForm({ ...singleEmailForm, body_html: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2 font-mono text-xs text-[#0F0F0F]" />
            </div>
            <button type="submit" disabled={singleEmailSubmitting} data-testid="single-email-submit" className="btn-terra disabled:opacity-40 w-full">
              {singleEmailSubmitting ? "Sending email..." : "Send Email"}
            </button>
          </form>
        </div>
      )}

      {/* Enterprise paywall */}
      {paywallOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center px-4" data-testid="enterprise-paywall">
          <div className="bg-[#FDFCF8] border border-[#0F0F0F] max-w-md w-full p-6 space-y-3">
            <div className="label-eyebrow flex items-center gap-1.5"><Lock className="w-3 h-3" /> Enterprise feature</div>
            <h2 className="font-display font-black text-2xl">Bulk handling.</h2>
            <p className="text-sm text-[#5C5C5C]">
              Update statuses, send emails, and export CSVs across dozens of applicants in seconds.
              Available on the Enterprise plan.
            </p>
            <div className="flex gap-2">
              <Link to="/billing" data-testid="paywall-upgrade" className="btn-terra">Upgrade to Enterprise</Link>
              <button onClick={() => setPaywallOpen(false)} className="btn-ghost text-sm">Maybe later</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
